import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useI18n } from "../i18n/I18nContext";
import SectionHead from "../components/SectionHead";

const ease = [0.16, 1, 0.3, 1];

export default function ToneCheck() {
  const { t } = useI18n();
  const [playing, setPlaying] = useState(false);
  const ctxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);

  const stop = () => {
    try {
      if (gainRef.current && ctxRef.current) {
        const now = ctxRef.current.currentTime;
        gainRef.current.gain.cancelScheduledValues(now);
        gainRef.current.gain.setValueAtTime(gainRef.current.gain.value, now);
        gainRef.current.gain.linearRampToValueAtTime(0.0001, now + 0.25);
      }
      if (oscRef.current) {
        oscRef.current.stop(ctxRef.current ? ctxRef.current.currentTime + 0.3 : 0);
      }
    } catch (e) { /* noop */ }
    setTimeout(() => {
      try { ctxRef.current && ctxRef.current.close(); } catch (e) { /* ignore */ }
      ctxRef.current = null; oscRef.current = null; gainRef.current = null;
    }, 350);
    setPlaying(false);
  };

  const toggle = () => {
    if (playing) { stop(); return; }
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === "suspended") ctx.resume().catch(() => { /* ignore */ });
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 4000;
      gain.gain.value = 0.0001;
      osc.connect(gain).connect(ctx.destination);
      const now = ctx.currentTime;
      gain.gain.exponentialRampToValueAtTime(0.06, now + 0.4);
      osc.start();
      ctxRef.current = ctx; oscRef.current = osc; gainRef.current = gain;
      setPlaying(true);
      setTimeout(() => { if (oscRef.current === osc) stop(); }, 6000);
    } catch (e) { /* noop */ }
  };

  useEffect(() => () => stop(), []);

  return (
    <section
      id="reminder"
      className="border-t hair py-24 md:py-32"
      data-testid="tone-check-section"
    >
      <div className="container-edge">
        <SectionHead
          kana={t.sections.reminder.kana}
          roman={t.sections.reminder.roman}
        />

        <div className="grid grid-cols-12 gap-y-10 md:gap-x-10 items-end">
          <motion.div
            className="col-span-12 md:col-span-7"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease }}
          >
            <h3 className="display text-[28px] md:text-[40px] mb-4">{t.reminder.heading}</h3>
            <p className="body-text" style={{ maxWidth: "48ch" }}>{t.reminder.sub}</p>
          </motion.div>

          <motion.div
            className="col-span-12 md:col-span-4 md:col-start-9 flex flex-col items-start gap-4"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease, delay: 0.1 }}
          >
            <button
              type="button"
              className="tone-btn"
              data-active={playing ? "true" : "false"}
              aria-pressed={playing}
              onClick={toggle}
              data-testid="tone-check-play-btn"
            >
              {playing ? t.reminder.stop : t.reminder.play}
            </button>
            <p className="mono" style={{ color: "var(--faded)", maxWidth: "34ch", lineHeight: 1.5 }}>
              {t.reminder.note}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
